import { http } from "@api/base";
import { generatePath } from "react-router-dom";
import { collegeSerializer, programSerializer } from "./serializers";

const apiPaths = {
  // College operations
  listColleges: "/courses/colleges",
  getCollege: "/courses/colleges/:collegeId",
  createCollege: "/courses/colleges",
  updateCollege: "/courses/colleges/:collegeId",
  deleteCollege: "/courses/colleges/:collegeId",
  // Program operations
  listPrograms: "/courses/programs",
  getProgram: "/courses/programs/:programId",
  createProgram: "/courses/programs",
  updateProgram: "/courses/programs/:programId",
  deleteProgram: "/courses/programs/:programId",
  // Test endpoint
};

/**
 * List all colleges
 */
const listColleges = async (query = "") => {
  const queryString = query ? `?${query}` : "";
  const response = await http.get(`${apiPaths.listColleges}${queryString}`);
  if (response.data) {
    response.data.data = collegeSerializer.fromListJSON(response.data.data);
  }
  return response;
};

/**
 * Get a specific college by ID
 */
const getCollege = async (collegeId, includePrograms = false) => {
  const query = includePrograms ? "?include_programs=true" : "";
  const path = generatePath(apiPaths.getCollege, { collegeId }) + query;
  const response = await http.get(path);
  if (response.data) {
    response.data.data = collegeSerializer.fromJSON(response.data.data);
  }
  return response;
};

/**
 * Create a new college
 */
const createCollege = async (collegeData) => {
  const backendPayload = collegeSerializer.toCreateJSON(collegeData);
  const response = await http.post(apiPaths.createCollege, backendPayload);
  if (response.data) {
    response.data.data = collegeSerializer.fromJSON(response.data.data);
  }
  return response;
};

/**
 * Update an existing college
 */
const updateCollege = async (collegeId, collegeData) => {
  const path = generatePath(apiPaths.updateCollege, { collegeId });
  const backendPayload = collegeSerializer.toUpdateJSON(collegeData);
  const response = await http.put(path, backendPayload);
  if (response.data) {
    response.data.data = collegeSerializer.fromJSON(response.data.data);
  }
  return response;
};

/**
 * Delete a college
 */
const deleteCollege = async (collegeId) => {
  const path = generatePath(apiPaths.deleteCollege, { collegeId });
  const response = await http.delete(path);
  return response;
};

/**
 * List all programs
 */
const listPrograms = async (query = "") => {
  const queryString = query ? `?${query}` : "";
  const response = await http.get(`${apiPaths.listPrograms}${queryString}`);
  if (response.data) {
    response.data.data = programSerializer.fromListJSON(response.data.data);
  }
  return response;
};

/**
 * Get a specific program by ID
 */
const getProgram = async (programId, includeCollege = false) => {
  const query = includeCollege ? "?include_college=true" : "";
  const path = generatePath(apiPaths.getProgram, { programId }) + query;
  const response = await http.get(path);
  if (response.data) {
    response.data.data = programSerializer.fromJSON(response.data.data);
  }
  return response;
};

/**
 * Create a new program - FIXED VERSION
 */
const createProgram = async (programData) => {
  // Ensure college_id is an integer and data is properly formatted
  const backendPayload = {
    name: programData.name?.trim(),
    code: programData.code?.trim()?.toUpperCase(),
    college_id: parseInt(programData.college_id, 10), // Convert to integer
  };

  const response = await http.post(apiPaths.createProgram, backendPayload);
  if (response.data) {
    response.data.data = programSerializer.fromJSON(response.data.data);
  }
  return response;
};

/**
 * Update an existing program - FIXED VERSION
 */
const updateProgram = async (programId, programData) => {
  const path = generatePath(apiPaths.updateProgram, { programId });

  // Ensure college_id is an integer and data is properly formatted
  const backendPayload = {
    name: programData.name?.trim(),
    code: programData.code?.trim()?.toUpperCase(),
    college_id: parseInt(programData.college_id, 10), // Convert to integer
  };

  const response = await http.put(path, backendPayload);
  if (response.data) {
    response.data.data = programSerializer.fromJSON(response.data.data);
  }
  return response;
};

/**
 * Delete a program
 */
const deleteProgram = async (programId) => {
  const path = generatePath(apiPaths.deleteProgram, { programId });
  const response = await http.delete(path);
  return response;
};

/**
 * Query Builder for colleges and programs
 */
class CollegeProgramQueryBuilder {
  constructor() {
    this.page = "";
    this.perPage = "";
    this.includeProgramsFlag = false;
    this.includeCollegeFlag = false;
    this.collegeId = null;
  }

  withPage(page = 1) {
    if (page > 0) this.page = `page=${page}`;
    return this;
  }

  withSize(size) {
    if (size > 0) this.perPage = `per_page=${size}`;
    return this;
  }

  includePrograms() {
    this.includeProgramsFlag = true;
    return this;
  }

  includeCollege() {
    this.includeCollegeFlag = true;
    return this;
  }

  forCollege(collegeId) {
    this.collegeId = collegeId;
    return this;
  }

  qs() {
    const queryParams = [];
    if (this.page) queryParams.push(this.page);
    if (this.perPage) queryParams.push(this.perPage);
    if (this.includeProgramsFlag) queryParams.push("include_programs=true");
    if (this.includeCollegeFlag) queryParams.push("include_college=true");
    if (this.collegeId) queryParams.push(`college_id=${this.collegeId}`);
    return queryParams.join("&");
  }
}

// Export the API object
export const collegeProgramApi = {
  // College operations
  listColleges,
  getCollege,
  createCollege,
  updateCollege,
  deleteCollege,
  // Program operations
  listPrograms,
  getProgram,
  createProgram,
  updateProgram,
  deleteProgram,
  // Query builder
  queryBuilder: () => new CollegeProgramQueryBuilder(),
};

/**
 * List all colleges with their programs
 */
collegeProgramApi.listCollegesWithPrograms = async (query = "") => {
  const queryBuilder = collegeProgramApi.queryBuilder();
  queryBuilder.includePrograms();
  const queryString = queryBuilder.qs();
  const response = await http.get(`${apiPaths.listColleges}?${queryString}`);
  if (response.data) {
    response.data.data = response.data.data.map((college) => ({
      ...collegeSerializer.fromJSON(college),
      programs: programSerializer.fromListJSON(college.programs || []),
    }));
  }
  return response;
};
